// Draws the live launch countdown onto the LED wall in the Leviathan stage
// photo, in the wall's perspective, so the numbers look like they are on the
// screen. Every strip of the wall is laid out at its on-screen size (the
// photo's scale is folded into the strip's homography) because a 3D layer's
// graphics memory follows its CSS size, and slide-sized strips crashed
// Safari on iPhones. The 2026 photo contains the bull and candles. The lettering and the
// mascots are image layers in the same transformed slices: the words and the
// ten digits were drawn by the image model on this wall's blue and cut out to
// transparent sprites (see leviathanStageWall.ts), and each digit pair is
// assembled from those glyphs every second.
//
// Inline styles only: the markup must render identically from
// react-dom/server.
import type { WallAsset } from "./leviathanStageWall";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  getLeviathanCountdown,
  type LeviathanCountdown,
} from "./leviathanCountdown";
import {
  digitGlyphBoxes,
  coverSizesForPhotoPx,
  fitPlacement,
  matrix3dFromHomography,
  parseObjectPosition,
  scaleHomography,
  WALL_PHOTO_PX_PER_SLIDE_PX,
  wallAsset,
  wallSrcSet,
  PHOTO_SIZE,
  SLIDE_SIZE,
  spriteBox,
  type ObjectPositionValue,
  WALL_LABELS_LAYOUT,
  WALL_LINE_PLACEMENT,
  WALL_LINE_TEXT,
  WALL_MASCOTS_LAYOUT,
  WALL_NUMBERS_LAYOUT,
  WALL_TITLE_PLACEMENT,
  WALL_TITLE_TEXT,
  wallSlices,
  wallSprite,
  type Placement,
  type WallSpriteBox,
} from "./leviathanStageWall";

export type LeviathanStageCountdownProps = {
  targetMs: number;
  initialRemaining: LeviathanCountdown;
  /**
   * When given, the wall shows this value and runs no timer of its own, so a
   * page that already ticks a countdown keeps both displays on the same
   * second.
   */
  remaining?: LeviathanCountdown;
  /** A photo other than the served one, as a plain image; the default is the AVIF, JPEG set in leviathanWallAssets.json. */
  src?: string;
  srcSet?: string;
  sizes?: string;
  /**
   * The `sizes` rule for an element `photoPx` photo px wide, so the browser
   * fetches the variant its screen needs. The default assumes the photo
   * covers a 16:9 frame; the homepage passes its own rule for the phone band.
   */
  sizesForPhotoPx?: (photoPx: number) => string;
  fit?: "cover" | "contain";
  position?: [number, number];
  /**
   * 'numbers' renders only the digits (for the photo that still has the rest
   * of the slide baked in); 'all' also renders the title, labels, line and
   * mascots for the 2026 photo.
   */
  liveText?: "numbers" | "all";
  /** Skips measuring; used for server rendering and the screenshot check. */
  placement?: Placement;
  className?: string;
  style?: CSSProperties;
};

// renderToStaticMarkup never runs effects, but React warns on
// useLayoutEffect during server rendering; swap it for useEffect there.
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

// Decorative image layer on the wall, placed by its slide-px box. AVIF for
// browsers that decode it (Chrome, Safari 16.4 and later), the fallback
// otherwise, each in the size the screen needs.
function WallImage({
  asset,
  box,
  kind,
  digit,
  mascot,
  xOffset = 0,
  scale = 1,
  sizes,
  fit,
}: {
  asset: WallAsset;
  box: WallSpriteBox;
  kind: "title" | "line" | "label" | "glyph" | "mascot";
  digit?: string;
  mascot?: string;
  xOffset?: number;
  /** The strip's px per slide px; the box is in slide px. */
  scale?: number;
  sizes: string;
  fit?: "contain";
}) {
  const fallback = asset.fallback[asset.fallback.length - 1]!;
  return (
    <picture style={{ display: "contents" }}>
      {asset.avif.length > 0 && <source type="image/avif" srcSet={wallSrcSet(asset.avif)} sizes={sizes} />}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={fallback.src}
        srcSet={wallSrcSet(asset.fallback)}
        sizes={sizes}
        alt=""
        aria-hidden
        draggable={false}
        decoding="async"
        data-wall={kind}
        data-digit={digit}
        data-mascot={mascot}
        style={{
          position: "absolute",
          left: (box.left - xOffset) * scale,
          top: box.top * scale,
          width: box.width * scale,
          height: box.height * scale,
          // The global stylesheet caps images at their parent's width, and a
          // digit pair's parent is a zero-width span.
          maxWidth: "none",
          ...(fit === "contain" ? { objectFit: "contain", objectPosition: "bottom center" } : {}),
        }}
      />
    </picture>
  );
}

// The words as text, invisible, so the wall's markup still carries them.
const HIDDEN_TEXT: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  opacity: 0,
};

// React 18 neither types nor recognizes fetchPriority; the lowercase form
// passes through as the HTML attribute without a warning.
const IMG_PRIORITY = { fetchpriority: "high" };

const PLATE = wallAsset("plate");
const PLATE_FALLBACK = PLATE.fallback[PLATE.fallback.length - 1]!;

export function LeviathanStageCountdown({
  targetMs,
  initialRemaining,
  remaining: controlledRemaining,
  src,
  srcSet,
  sizes = "100vw",
  sizesForPhotoPx = coverSizesForPhotoPx,
  fit = "cover",
  position,
  liveText = "numbers",
  placement,
  className,
  style,
}: LeviathanStageCountdownProps) {
  const positionX = position ? position[0] : 0.5;
  const positionY = position ? position[1] : 0.5;
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [ownRemaining, setRemaining] = useState<LeviathanCountdown>(initialRemaining);
  const remaining = controlledRemaining ?? ownRemaining;
  const [measured, setMeasured] = useState<Placement | null>(null);
  const controlled = controlledRemaining !== undefined;

  useEffect(() => {
    if (controlled) return;
    const tick = () => setRemaining(getLeviathanCountdown(Date.now(), targetMs));
    tick();
    // Align updates to the second boundary so the seconds digit never skips.
    let intervalId: number | undefined;
    const timeoutId = window.setTimeout(() => {
      tick();
      intervalId = window.setInterval(tick, 1000);
    }, 1000 - (Date.now() % 1000));
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [targetMs, controlled]);

  useIsomorphicLayoutEffect(() => {
    if (placement) return;
    const container = containerRef.current;
    if (!container) return;
    const measure = (width: number, height: number) => {
      // Pages restyle the hero image through CSS (the homepage overlay sets
      // object-position in a media query with !important), so the element's
      // computed style is the truth at measurement time, the props only the
      // fallback.
      let effectiveFit: "cover" | "contain" = fit;
      let posX: ObjectPositionValue = positionX;
      let posY: ObjectPositionValue = positionY;
      const img = imgRef.current;
      if (img) {
        const computed = getComputedStyle(img);
        if (computed.objectFit === "cover" || computed.objectFit === "contain") {
          effectiveFit = computed.objectFit;
        }
        if (computed.objectPosition) {
          [posX, posY] = parseObjectPosition(computed.objectPosition);
        }
      }
      setMeasured(fitPlacement(width, height, effectiveFit, posX, posY));
    };
    if (typeof ResizeObserver === "undefined") {
      measure(container.clientWidth, container.clientHeight);
      return;
    }
    // The observer's initial callback does the first measurement. Entry sizes
    // are fractional and unaffected by ancestor CSS transforms. A media query
    // that changes object-position comes with a container resize, so
    // re-reading the computed style inside this callback keeps the frame in
    // sync with the CSS-positioned image.
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      const boxSize = entry.contentBoxSize?.[0];
      measure(
        boxSize ? boxSize.inlineSize : entry.contentRect.width,
        boxSize ? boxSize.blockSize : entry.contentRect.height
      );
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [placement, fit, positionX, positionY]);

  const framePlacement = placement ?? measured;
  const frameScale = framePlacement?.scale ?? 1;
  const numberTexts = [
    remaining.days > 99 ? String(remaining.days) : pad2(remaining.days),
    pad2(remaining.hours),
    pad2(remaining.minutes),
    pad2(remaining.seconds),
  ];
  const timerLabel =
    `Countdown to launch: ${remaining.days} days, ${remaining.hours} hours, ` +
    `${remaining.minutes} minutes, ${remaining.seconds} seconds`;
  const slices = wallSlices();

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        ...style,
      }}
    >
      {/* The hero image shows from the server-rendered HTML, before
          hydration and before any measurement: CSS object-fit places it, the
          JS-scaled frame carries only the wall slices. The served photo is
          AVIF with a JPEG fallback in four widths; a custom src is one file. */}
      <picture style={{ display: "contents" }}>
        {!src && <source type="image/avif" srcSet={wallSrcSet(PLATE.avif)} sizes={sizes} />}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imgRef}
          src={src ?? PLATE_FALLBACK.src}
          srcSet={src ? srcSet : wallSrcSet(PLATE.fallback)}
          sizes={src && !srcSet ? undefined : sizes}
          alt=""
          width={PHOTO_SIZE.width}
          height={PHOTO_SIZE.height}
          decoding="async"
          draggable={false}
          data-wall="plate"
          {...IMG_PRIORITY}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: fit,
            objectPosition: `${positionX * 100}% ${positionY * 100}%`,
            display: "block",
          }}
        />
      </picture>
      {/* The frame is only moved to the photo's placement. The photo's
          scale is folded into every strip's own coordinates instead (see
          scaleHomography), so each strip's graphics backing store is its
          on-screen size rather than the 2904 by 1444 slide it maps. */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: PHOTO_SIZE.width * frameScale,
          height: PHOTO_SIZE.height * frameScale,
          transform: framePlacement
            ? `translate(${framePlacement.offsetX}px, ${framePlacement.offsetY}px)`
            : undefined,
          // Nothing flashes in the wrong place before the first measurement.
          visibility: framePlacement ? undefined : "hidden",
        }}
      >
        {slices.map((slice, index) => {
          const isLast = index === slices.length - 1;
          const sliceLeft = slice.u0 * SLIDE_SIZE.width;
          // Each strip except the last overlaps the next by 2 slide px so
          // rounding the scaled strips to device pixels leaves no gap.
          const sliceRight = slice.u1 * SLIDE_SIZE.width + (isLast ? 0 : 2);
          const stripMatrix = matrix3dFromHomography(scaleHomography(slice.homography, frameScale));
          const sizesFor = (box: WallSpriteBox) => sizesForPhotoPx(box.width * WALL_PHOTO_PX_PER_SLIDE_PX);
          const visible = (box: WallSpriteBox) =>
            box.left < sliceRight && box.left + box.width > sliceLeft;
          const title = wallSprite(WALL_TITLE_PLACEMENT.sprite);
          const titleBox = spriteBox(title, WALL_TITLE_PLACEMENT.cx, WALL_TITLE_PLACEMENT.cy, WALL_TITLE_PLACEMENT.inkHeight);
          const line = wallSprite(WALL_LINE_PLACEMENT.sprite);
          const lineBox = spriteBox(line, WALL_LINE_PLACEMENT.cx, WALL_LINE_PLACEMENT.cy, WALL_LINE_PLACEMENT.inkHeight);
          return (
            <div
              key={index}
              role={index === 0 ? "timer" : undefined}
              aria-live={index === 0 ? "off" : undefined}
              aria-label={index === 0 ? timerLabel : undefined}
              aria-hidden={index === 0 ? undefined : true}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                // One strip, at its on-screen size. The extra height keeps
                // the mascots' feet below the LED wall.
                width: (sliceRight - sliceLeft) * frameScale,
                height: (SLIDE_SIZE.height + 100) * frameScale,
                transformOrigin: "0 0",
                transform: `${stripMatrix} translateX(${sliceLeft * frameScale}px)`,
                overflow: "hidden",
              }}
            >
              {liveText === "all" && WALL_MASCOTS_LAYOUT.filter(visible).map(({ name, ...box }) => (
                <WallImage key={name} asset={wallAsset(`mascot-${name}`)} box={box} kind="mascot" mascot={name} xOffset={sliceLeft} scale={frameScale} sizes={sizesFor(box)} fit="contain" />
              ))}
              {numberTexts.map((text, pair) => (
                <span key={pair} aria-hidden data-wall="digit" style={{ position: "absolute", left: 0, top: 0 }}>
                  {digitGlyphBoxes(
                    text,
                    WALL_NUMBERS_LAYOUT.xCenters[pair]!,
                    WALL_NUMBERS_LAYOUT.yCenters[pair] ?? WALL_NUMBERS_LAYOUT.y,
                    WALL_NUMBERS_LAYOUT.inkHeight,
                    WALL_NUMBERS_LAYOUT.gapEm
                  ).filter(({ box }) => visible(box)).map(({ digit, box }, glyph) => (
                    <WallImage key={glyph} asset={wallSprite(`digit-${digit}`)} box={box} kind="glyph" digit={digit} xOffset={sliceLeft} scale={frameScale} sizes={sizesFor(box)} />
                  ))}
                  <span style={HIDDEN_TEXT}>{text}</span>
                </span>
              ))}
              {liveText === "all" && (
                <>
                  {WALL_LABELS_LAYOUT.sprites.map((name, pair) => {
                    const box = spriteBox(wallSprite(name), WALL_LABELS_LAYOUT.xCenters[pair]!, WALL_LABELS_LAYOUT.y, WALL_LABELS_LAYOUT.inkHeight);
                    return visible(box) ? <WallImage key={name} asset={wallSprite(name)} box={box} kind="label" xOffset={sliceLeft} scale={frameScale} sizes={sizesFor(box)} /> : null;
                  })}
                  {visible(titleBox) && <WallImage asset={title} box={titleBox} kind="title" xOffset={sliceLeft} scale={frameScale} sizes={sizesFor(titleBox)} />}
                  {visible(lineBox) && <WallImage asset={line} box={lineBox} kind="line" xOffset={sliceLeft} scale={frameScale} sizes={sizesFor(lineBox)} />}
                  <span aria-hidden data-wall="text" style={HIDDEN_TEXT}>
                    {WALL_TITLE_TEXT} {WALL_LABELS_LAYOUT.texts.join(" ")} {WALL_LINE_TEXT}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LeviathanStageCountdown;
